import ReactECharts from 'echarts-for-react';
import { BarChart2 } from 'lucide-react';
import { useCashFlow12m } from '@/hooks/useFinancialData';
import { formatBRLCompact } from '@/lib/format';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';

export function CashFlowChart() {
  const { data, loading } = useCashFlow12m();

  const months   = data.map((m) => m.month);
  const revenues = data.map((m) => m.revenue / 100);
  const expenses = data.map((m) => m.expense / 100);
  const balance  = data.map((_m, i) =>
    data.slice(0, i + 1).reduce((s, x) => s + (x.revenue - x.expense) / 100, 0)
  );

  const option = {
    grid: { top: 30, right: 16, bottom: 24, left: 56 },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15,61,46,0.95)',
      borderColor: 'rgba(15,61,46,0.95)',
      textStyle: { color: '#F8FAFC' },
      valueFormatter: (v: number) => formatBRLCompact(Math.round(v * 100)),
    },
    legend: {
      data: ['Receitas', 'Despesas', 'Saldo acumulado'],
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: '#64748b', fontSize: 11 },
      bottom: 0,
    },
    xAxis: {
      type: 'category',
      data: months,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#E2E8F0' } },
      axisLabel: { color: '#94A3B8', fontSize: 11 },
    },
    yAxis: [
      {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#F1F5F9' } },
        axisLabel: {
          color: '#94A3B8',
          fontSize: 11,
          formatter: (v: number) => formatBRLCompact(Math.round(v * 100)),
        },
      },
    ],
    series: [
      {
        name: 'Receitas',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        data: revenues,
        itemStyle: { color: '#14a655' },
        lineStyle: { width: 2.5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(20,166,85,0.20)' },
              { offset: 1, color: 'rgba(20,166,85,0)' },
            ],
          },
        },
      },
      {
        name: 'Despesas',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        data: expenses,
        itemStyle: { color: '#dc2626' },
        lineStyle: { width: 2.5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(220,38,38,0.18)' },
              { offset: 1, color: 'rgba(220,38,38,0)' },
            ],
          },
        },
      },
      {
        name: 'Saldo acumulado',
        type: 'line',
        smooth: true,
        symbol: 'none',
        data: balance,
        itemStyle: { color: '#0f3d2e' },
        lineStyle: { width: 1.5, type: 'dashed' },
      },
    ],
  };

  return (
    <div className="card p-5">
      <SectionHeader
        title="Fluxo de caixa — últimos 12 meses"
        subtitle="Receitas, despesas e saldo acumulado"
      />
      {loading ? (
        <div className="h-80 rounded-xl bg-surface-subtle animate-pulse" />
      ) : data.length === 0 ? (
        <EmptyState
          icon={BarChart2}
          title="Sem dados de fluxo de caixa"
          description="Importe seu extrato para visualizar as movimentações dos últimos 12 meses."
        />
      ) : (
        <ReactECharts option={option} style={{ height: 320 }} notMerge lazyUpdate />
      )}
    </div>
  );
}
